import {Screens} from '../../routing';
import {LocalParamList, NavigationRoute} from './components/types';
import {Props} from './model';
import React from 'react';
import {View, Platform, Animated, LayoutChangeEvent, StyleSheet} from 'react-native';
import {CommonActions, NavigationContext, NavigationRouteContext, useLinkBuilder} from '@react-navigation/native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useStyles} from '../../hooks/styles';
import {images} from '../../../assets/icons';
import {useTheme} from '../../theme';
import {useIsKeyboardShown} from '../../hooks/useIsKeyboardShown';
import {BottomTabItem} from './components/BottomTabItem';
import {tabBarTopOffset} from './variables';
import {useDimensions} from '../../hooks/useDimensions';

const DEFAULT_TABBAR_HEIGHT = 49;
const DEFAULT_MAX_TAB_ITEM_WIDTH = 125;

const useNativeDriver = Platform.OS !== 'web';

export default function BottomTabBar({
  state,
  navigation,
  descriptors,
  adaptive = true,
  keyboardHidesTabBar = true,
  labelPosition,
  safeAreaInsets,
  style,
  tabStyle,
  customPressHandlers = {},
}: Props) {
  const buildLink = useLinkBuilder();

  const focusedRoute = state.routes[state.index];
  const focusedDescriptor = descriptors[focusedRoute.key];
  const focusedOptions = focusedDescriptor.options;

  const dimensions = useDimensions();
  const isKeyboardShown = useIsKeyboardShown();

  const shouldShowTabBar = focusedOptions.tabBarVisible !== false && !(keyboardHidesTabBar && isKeyboardShown);

  const visibilityAnimationConfigRef = React.useRef(focusedOptions.tabBarVisibilityAnimationConfig);

  React.useEffect(() => {
    visibilityAnimationConfigRef.current = focusedOptions.tabBarVisibilityAnimationConfig;
  });

  const [isTabBarHidden, setIsTabBarHidden] = React.useState(!shouldShowTabBar);

  const [visible] = React.useState(() => new Animated.Value(shouldShowTabBar ? 1 : 0));

  React.useEffect(() => {
    const visibilityAnimationConfig = visibilityAnimationConfigRef.current;

    if (shouldShowTabBar) {
      const animation = visibilityAnimationConfig?.show?.animation === 'spring' ? Animated.spring : Animated.timing;

      animation(visible, {
        toValue: 1,
        useNativeDriver,
        duration: 100,
        ...visibilityAnimationConfig?.show?.config,
      }).start(({finished}) => {
        if (finished) {
          setIsTabBarHidden(false);
        }
      });
    } else {
      setIsTabBarHidden(true);

      const animation = visibilityAnimationConfig?.hide?.animation === 'spring' ? Animated.spring : Animated.timing;

      animation(visible, {
        toValue: 0,
        useNativeDriver,
        duration: 100,
        ...visibilityAnimationConfig?.hide?.config,
      }).start();
    }
  }, [visible, shouldShowTabBar]);

  const [layout, setLayout] = React.useState({
    height: 0,
    width: dimensions.width,
  });

  const handleLayout = (e: LayoutChangeEvent) => {
    const {height, width} = e.nativeEvent.layout;

    setLayout((layoutValue) => {
      if (height === layoutValue.height && width === layoutValue.width) {
        return layoutValue;
      } else {
        return {
          height,
          width,
        };
      }
    });
  };

  // @ts-ignore
  const routes: NavigationRoute<LocalParamList, Screens>[] = state.routes;
  const shouldUseHorizontalLabels = () => {
    if (labelPosition) {
      return labelPosition === 'beside-icon';
    }

    if (!adaptive) {
      return false;
    }

    if (layout.width >= 768) {
      // Screen size matches a tablet
      let maxTabItemWidth = DEFAULT_MAX_TAB_ITEM_WIDTH;

      const flattenedStyle = StyleSheet.flatten(tabStyle);

      if (flattenedStyle) {
        if (typeof flattenedStyle.width === 'number') {
          maxTabItemWidth = flattenedStyle.width;
        } else if (typeof flattenedStyle.maxWidth === 'number') {
          maxTabItemWidth = flattenedStyle.maxWidth;
        }
      }

      return routes.length * maxTabItemWidth <= layout.width;
    } else {
      const isLandscape = dimensions.width > dimensions.height;

      return isLandscape;
    }
  };

  const defaultInsets = useSafeAreaInsets();

  const insets = {
    top: safeAreaInsets?.top ?? defaultInsets.top,
    right: safeAreaInsets?.right ?? defaultInsets.right,
    bottom: safeAreaInsets?.bottom ?? defaultInsets.bottom,
    left: safeAreaInsets?.left ?? defaultInsets.left,
  };

  const styles = useStyles(({theme, addShadow}) => ({
    tabBar: {
      bottom: 0,
      left: 0,
      right: 0,
      position: 'relative',
      paddingBottom: 10,
    },
    content: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      top: 0,
      flex: 1,
      flexDirection: 'row',
    },
    contentList: {
      zIndex: 1,
      position: 'absolute',
      height: DEFAULT_TABBAR_HEIGHT + tabBarTopOffset,
      left: 0,
      bottom: 0,
      right: 0,
      top: 0,
      flexDirection: 'row',
      elevation: 100,
    },
    background: {
      position: 'absolute',
      left: 0,
      top: tabBarTopOffset,
      right: 0,
      flexDirection: 'row',
      alignItems: 'stretch',
      zIndex: 0,
      height: 66,
      ...addShadow({
        height: -4,
        radius: 13,
        opacity: 0.13,
      }),
    },
    backgroundMain: {
      zIndex: 0,
      position: 'absolute',
      left: 0,
      right: 0,
      top: tabBarTopOffset,
      height: 100,
      backgroundColor: theme.color.grayMenu,
    },
    backgroundBottom: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: 65,
      height: 100,
      zIndex: 1,
      backgroundColor: theme.color.primaryNonGreyBackground,
    },
    backgroundFilling: {
      backgroundColor: theme.color.primaryNonGreyBackground,
      flex: 1,
      elevation: 20,
      height: 57,
    },
    backgroundLeftCircle: {
      flexBasis: 56,
      flexGrow: 0,
      flexShrink: 0,
      elevation: 20,
    },
    backgroundRightCircle: {
      flexBasis: 56,
      flexGrow: 0,
      flexShrink: 0,
      elevation: 20,
    },
  }));

  const theme = useTheme();

  return (
    <Animated.View
      style={[
        styles.tabBar,
        // eslint-disable-next-line react-native/no-inline-styles
        {
          transform: [
            {
              translateY: visible.interpolate({
                inputRange: [0, 1],
                outputRange: [layout.height, 0],
              }),
            },
          ],
          position: isTabBarHidden ? 'absolute' : (null as any),
        },
        {
          height:
            DEFAULT_TABBAR_HEIGHT + tabBarTopOffset + (Platform.OS === 'android' || Platform.OS === 'ios' ? 8 : 0),
          paddingHorizontal: Math.max(insets.left, insets.right),
        },
        style,
      ]}
      pointerEvents={isTabBarHidden ? 'none' : 'auto'}>
      <View style={styles.content} onLayout={handleLayout}>
        <View style={styles.backgroundMain} />
        <View style={styles.background}>
          <View style={styles.backgroundFilling} />
          <View style={styles.backgroundLeftCircle}>
            <images.TabBarCircle
              width={56}
              height={56}
              color={theme.color.primaryNonGreyBackground}
              grayedColor={theme.color.primaryNonGreyBackground}
            />
          </View>
          <View style={styles.backgroundRightCircle}>
            <images.TabBarCircleRight
              width={56}
              height={56}
              color={theme.color.primaryNonGreyBackground}
              grayedColor={theme.color.primaryNonGreyBackground}
            />
          </View>
          <View style={styles.backgroundFilling} />
        </View>
        <View style={styles.backgroundBottom} />
        <View style={styles.contentList}>
          {routes.map((route, index) => {
            const focused = index === state.index;
            const {options} = descriptors[route.key];

            const onPress = () => {
              if (
                customPressHandlers[route.name] !== undefined &&
                typeof customPressHandlers[route.name] === 'function'
              ) {
                customPressHandlers[route.name]();
              } else {
                const event = navigation.emit({
                  type: 'tabPress',
                  target: route.key,
                  canPreventDefault: true,
                });

                if (!focused && !event.defaultPrevented) {
                  navigation.dispatch({
                    ...CommonActions.navigate(route.name),
                    target: state.key,
                  });
                }
              }
            };

            const onLongPress = () => {
              navigation.emit({
                type: 'tabLongPress',
                target: route.key,
              });
            };

            return (
              <NavigationContext.Provider key={route.key} value={descriptors[route.key].navigation}>
                <NavigationRouteContext.Provider value={route}>
                  <BottomTabItem
                    focused={focused}
                    route={route}
                    icon={options.tabBarIcon}
                    to={buildLink(route.name, route.params)}
                    button={options.tabBarButton}
                    onPress={onPress}
                    onLongPress={onLongPress}
                    horizontal={shouldUseHorizontalLabels()}
                    style={tabStyle}
                  />
                </NavigationRouteContext.Provider>
              </NavigationContext.Provider>
            );
          })}
        </View>
      </View>
    </Animated.View>
  );
}
